import dynamic from 'next/dynamic';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { twJoin } from 'tailwind-merge';

import Avatar from 'shopper/components/Avatar';
import Box from 'shopper/components/Box';
import Button from 'shopper/components/Button';
import Icon from 'shopper/components/Icon';

import Anchor from 'components/Anchor';
import Image from 'components/Image';

import useDialog from 'hooks/useDialog';
import useTopScrollPosition from 'hooks/useTopScrollPosition';

import { sendEvent } from 'lib/gtag';
import { getUserAvatarUrl, toUserAvatarAltText } from 'lib/image';
import { toUserProfileLink } from 'lib/links';
import placeholder from 'lib/placeholder';

import { useUser } from 'providers/UserProvider';

import TopbarMobileBottom from './TopbarMobileBottom';

import APP from 'constants/app';
import IMAGE from 'constants/image';
import LINKS from 'constants/links';

const MenuDialog = dynamic(() => import('./MenuDialog'), {
  ssr: false,
});

const LoginDialog = dynamic(() => import('components/LoginDialog'), {
  ssr: false,
});

const TopbarMobile = ({ gaEventCategory }) => {
  const router = useRouter();
  const { isLoggedIn, LoggedInUser, loadingLoggedInUser } = useUser();
  const { showDialog } = useDialog();
  const [isTopScrollPosition] = useTopScrollPosition();

  const onMenuDialog = () => {
    showDialog(MenuDialog, {
      gaEventCategory: 'dropdown_mobile',
    });
  };

  const onLogoClick = () => {
    if (router.asPath === LINKS.HOME) {
      window.location.reload();
      return;
    }

    router.push(LINKS.HOME);
  };

  const onSignUpButtonClick = () => {
    sendEvent({
      action: 'signup__click',
      category: gaEventCategory,
    });

    showDialog(LoginDialog);
  };

  const onUserProfileClick = () => {
    sendEvent({
      action: 'user_profile__click',
      category: gaEventCategory,
    });
  };

  return (
    <>
      <Box
        className={twJoin(
          'relative z-40 flex-row items-center justify-between rounded-none border-b p-3 dark:border-neutral-low-300 lg:hidden',
          !isTopScrollPosition && 'shadow-[0px_0px_10px_0px_rgba(87,87,87,0.2)]'
        )}
      >
        <div className="flex items-center justify-center gap-1">
          <Button
            aria-label="Abrir menu"
            icon={<Icon name="menu" />}
            size="size2"
            type="neutral-ghost"
            onClick={onMenuDialog}
          />
          <button onClick={onLogoClick}>
            <Image
              alt="Promobit"
              className="mt-1 dark:brightness-0 dark:invert"
              height={28}
              loading={null}
              sizes="(max-width: 1024px) 40px, 1px"
              src={IMAGE.LOGO_PATH}
              srcSet={`${APP.LOCAL_IMAGE_PATH}/general/placeholder.jpg 1w, ${IMAGE.LOGO_PATH} 40w`}
              width={148}
            />
          </button>
        </div>
        {!loadingLoggedInUser && isLoggedIn ? (
          <button className="flex justify-center" onClick={onUserProfileClick}>
            <NextLink
              href={toUserProfileLink(LoggedInUser.userUsername)}
              prefetch={false}
              passHref
            >
              <Anchor>
                <Avatar
                  description={toUserAvatarAltText(LoggedInUser.userName)}
                  size="size5"
                  src={getUserAvatarUrl(LoggedInUser.userPhoto)}
                />
              </Anchor>
            </NextLink>
          </button>
        ) : (
          <Button
            className="[&>span]:text-xs"
            size="size3"
            onClick={onSignUpButtonClick}
          >
            {placeholder('actions.signupShort2')}
          </Button>
        )}
      </Box>
      <TopbarMobileBottom
        gaEventCategory={gaEventCategory}
        isTopScrollPosition={isTopScrollPosition}
      />
    </>
  );
};

TopbarMobile.propTypes = {
  gaEventCategory: PropTypes.string.isRequired,
};

export default TopbarMobile;
